import { useCallback, useEffect, useRef, useState } from "react";
import ReactModal from "react-modal";
import { PageHeader } from "../../page-header";
import { usePagamentoLote } from "../../../hooks/usePagamentoLote";
import { BoxModal } from "../../boxModal";
import { formatValueToMonetary } from "../../../utils/formatter";
import { toast } from "react-toastify";
import { TbLayoutList, TbCheck, TbAlertTriangle, TbClock, TbCalendarCheck, TbSearch, TbTrash } from "react-icons/tb";
import { useSearchParams } from "react-router-dom";
import { useAccount } from "../../../hooks/useAccount";
import { useAuth } from "../../../hooks/useAuth";
import { userHasPermission } from "../../../utils/verifyUserPermission";
import { companyUserPermissions } from "../../../enums/companyUserPermissions";
import "../PagamentoLote.css";
import "./PagamentoLoteAprovacao.css";

function dateBR(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr + "T00:00:00").toLocaleDateString("pt-BR");
}

function typeIcon(type) {
  const icons = { Boleto: "▥", PIX: "❖", Tributo: "▣", TED: "↔" };
  return icons[type] || "▣";
}

function badgeClass(status) {
  const map = { PENDENTE: "pendente", ALERTA: "alerta", PAGO: "aprovado", FALHOU: "alerta", AGENDADO: "agendado" };
  return map[status] || "pendente";
}

const PAYMENT_STATUS_LABELS = {
  PENDENTE: "Pendente",
  PAGO: "Pago",
  ALERTA: "Com alerta",
  FALHOU: "Falhou",
  AGENDADO: "Agendado",
};

const FILTERS = ["todos", "PENDENTE", "PAGO", "ALERTA", "FALHOU"];
const FILTER_LABELS = { todos: "Todos", PENDENTE: "Pendentes", PAGO: "Pagos", ALERTA: "Com alerta", FALHOU: "Falhos" };

export default function PagamentoLoteAprovacao() {
  const {
    payments,
    batches,
    currentFilter,
    setCurrentFilter,
    searchTerm,
    setSearchTerm,
    filteredPayments,
    toggleItem,
    toggleAll,
    approveSelected,
    sendBatchMfa,
    removePayment,
    loadPayments,
    stats,
  } = usePagamentoLote();

  const [searchParams, setSearchParams] = useSearchParams();
  const { avaliableBalance, getBalance } = useAccount();
  const { user } = useAuth();
  const batchIdFromUrl = searchParams.get("batchId") || "";
  const [selectedBatchId, setSelectedBatchId] = useState(batchIdFromUrl);

  // MFA modal
  const [mfaModalOpen, setMfaModalOpen] = useState(false);
  const [mfaCode, setMfaCode] = useState("");
  const [mfaDisabled, setMfaDisabled] = useState(false);
  const [mfaCountdown, setMfaCountdown] = useState(0);
  const mfaIntervalRef = useRef(null);

  const openBatches = batches.filter(
    (b) => b.status === "AGUARDANDO_APROVACAO" || b.status === "RASCUNHO"
  );

  const currentBatch = batches.find((b) => String(b.id) === String(selectedBatchId));

  // Permission logic:
  // - isMainAccess: can always approve
  // - sub-access: needs APPROVE_BATCH_PAYMENT permission AND must not be the one who submitted
  const hasApprovePermission = user?.isMainAccess || userHasPermission(user, companyUserPermissions.APPROVE_BATCH_PAYMENT);
  const isOwnBatch = !user?.isMainAccess && currentBatch?.submittedBy === user?.companyUser?.id;
  const canApprove = hasApprovePermission && !isOwnBatch;

  let approveBlockReason = null;
  if (!hasApprovePermission) approveBlockReason = "Você não tem permissão para aprovar pagamentos em lote";
  else if (isOwnBatch) approveBlockReason = "O responsável pelo envio do lote não pode aprová-lo";

  useEffect(() => {
    if (selectedBatchId) {
      loadPayments(selectedBatchId);
      getBalance();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBatchId]);

  // cleanup countdown interval on unmount
  useEffect(() => () => { if (mfaIntervalRef.current) clearInterval(mfaIntervalRef.current); }, []);

  function handleBatchSelect(id) {
    setSelectedBatchId(id);
    setSearchParams(id ? { batchId: id } : {});
  }

  const visible = filteredPayments();

  const countByStatus = useCallback(
    (status) => payments.filter((p) => p.status === status).length,
    [payments]
  );

  async function handleRelease() {
    if (!selectedBatchId) {
      toast.warning("Selecione um lote antes de aprovar.");
      return;
    }
    if (stats.selected.count === 0) {
      toast.warning("Selecione ao menos uma conta para aprovar.");
      return;
    }
    if (avaliableBalance !== undefined && stats.selected.amount > avaliableBalance) {
      toast.error(
        `Saldo insuficiente. Você selecionou ${formatValueToMonetary(stats.selected.amount)}, mas o saldo disponível é de ${formatValueToMonetary(avaliableBalance)}.`
      );
      return;
    }
    // Send MFA code and open modal
    const result = await sendBatchMfa();
    if (!result?.ok) return;
    setMfaCode("");
    setMfaDisabled(true);
    setMfaCountdown(40);
    setMfaModalOpen(true);

    if (mfaIntervalRef.current) clearInterval(mfaIntervalRef.current);
    mfaIntervalRef.current = setInterval(() => {
      setMfaCountdown((prev) => {
        if (prev <= 1) { clearInterval(mfaIntervalRef.current); setMfaDisabled(false); return 0; }
        return prev - 1;
      });
    }, 1000);
  }

  async function handleResendMfa() {
    const result = await sendBatchMfa();
    if (!result?.ok) return;
    toast.success("Novo código enviado!");
    setMfaDisabled(true);
    setMfaCountdown(40);
    if (mfaIntervalRef.current) clearInterval(mfaIntervalRef.current);
    mfaIntervalRef.current = setInterval(() => {
      setMfaCountdown((prev) => {
        if (prev <= 1) { clearInterval(mfaIntervalRef.current); setMfaDisabled(false); return 0; }
        return prev - 1;
      });
    }, 1000);
  }

  async function handleConfirmMfa() {
    if (!mfaCode) {
      toast.error("Insira o código de confirmação recebido por SMS.");
      return;
    }
    const result = await approveSelected(selectedBatchId, mfaCode);
    if (result?.ok) {
      setMfaModalOpen(false);
      setMfaCode("");
    }
  }

  return (
    <>
    <div className="paylote-page">
      <div className="paylote-inner col-md-10 m-auto mt-4">
        <div className="mb-3">
          <PageHeader
            title="Aprovação de lotes"
            pages={[
              { name: "Pagamento em Lote", link: "/pagamento-lote/dashboard" },
              { name: "Aprovação" },
            ]}
          />
        </div>

        {/* Batch selector */}
        {!selectedBatchId && (
          <section className="paylote-section-card" style={{ marginBottom: "1.25rem" }}>
            <div className="paylote-section-header">
              <div>
                <h2>Selecionar lote</h2>
                <p className="paylote-subtitle">Escolha qual lote deseja revisar e aprovar.</p>
              </div>
            </div>
            <div className="paylote-form-field" style={{ maxWidth: 480 }}>
              <label>Lote aguardando aprovação</label>
              <select value={selectedBatchId} onChange={(e) => handleBatchSelect(e.target.value)}>
                <option value="">— Selecione um lote —</option>
                {openBatches.map((b) => (
                  <option key={b.id} value={b.id}>
                    #{b.id} — {b.createdByName} — {b.totalBills} conta{b.totalBills !== 1 ? "s" : ""} ({b.status})
                  </option>
                ))}
              </select>
              {openBatches.length === 0 && (
                <span className="paylote-novo-field-hint">Nenhum lote aguardando aprovação no momento.</span>
              )}
            </div>
          </section>
        )}

        {/* Page head + lot info */}
        {currentBatch && (
        <section className="paylote-page-head">
          <div className="paylote-page-title">
            <h1>
              Aprovação do lote
              <span className="paylote-status-pill">{currentBatch.status}</span>
            </h1>
            <p className="paylote-created-by">
              Criado por: {currentBatch.createdByName}{currentBatch.createdByEmail ? ` (${currentBatch.createdByEmail})` : ""}
            </p>
            <button
              className="paylote-btn-secondary"
              style={{ marginTop: "0.5rem", width: "fit-content" }}
              onClick={() => handleBatchSelect("")}
            >
              Trocar lote
            </button>
          </div>

          <div className="paylote-lot-info">
            <h3>Informações do lote</h3>
            <div className="paylote-info-row">
              <span>ID do lote:</span>
              <strong>#{currentBatch.id}</strong>
            </div>
            <div className="paylote-info-row">
              <span>Data de criação:</span>
              <strong>{currentBatch.createdAt ? new Date(currentBatch.createdAt).toLocaleString("pt-BR") : "—"}</strong>
            </div>
            <div className="paylote-info-row">
              <span>Total de contas:</span>
              <strong>{currentBatch.totalBills ?? payments.length}</strong>
            </div>
            <div className="paylote-info-row">
              <span>Total do lote:</span>
              <strong>{formatValueToMonetary(currentBatch.totalAmount ?? stats.total.amount)}</strong>
            </div>
          </div>
        </section>
        )}

        {/* Metrics + Table only shown when a batch is selected */}
        {selectedBatchId && (<>
        <section className="paylote-metrics">
          <div className="paylote-metric-card">
            <div className="paylote-metric-icon blue"><TbLayoutList size={20} /></div>
            <div>
              <div className="paylote-metric-label">Total do lote</div>
              <div className="paylote-metric-value">{formatValueToMonetary(stats.total.amount)}</div>
              <div className="paylote-metric-small">{stats.total.count} contas</div>
            </div>
          </div>
          <div className="paylote-metric-card">
            <div className="paylote-metric-icon green"><TbCheck size={20} /></div>
            <div>
              <div className="paylote-metric-label">Aprovadas</div>
              <div className="paylote-metric-value">{stats.approved.count}</div>
              <div className="paylote-metric-small">{formatValueToMonetary(stats.approved.amount)}</div>
            </div>
          </div>
          <div className="paylote-metric-card">
            <div className="paylote-metric-icon orange"><TbClock size={20} /></div>
            <div>
              <div className="paylote-metric-label">Pendentes</div>
              <div className="paylote-metric-value">{stats.pending.count}</div>
              <div className="paylote-metric-small">{formatValueToMonetary(stats.pending.amount)}</div>
            </div>
          </div>
          <div className="paylote-metric-card">
            <div className="paylote-metric-icon red"><TbAlertTriangle size={20} /></div>
            <div>
              <div className="paylote-metric-label">Com alerta</div>
              <div className="paylote-metric-value">{stats.alert.count}</div>
              <div className="paylote-metric-small">{formatValueToMonetary(stats.alert.amount)}</div>
            </div>
          </div>
          <div className="paylote-metric-card">
            <div className="paylote-metric-icon purple"><TbCalendarCheck size={20} /></div>
            <div>
              <div className="paylote-metric-label">Agendadas</div>
              <div className="paylote-metric-value">{stats.scheduled.count}</div>
              <div className="paylote-metric-small">{formatValueToMonetary(stats.scheduled.amount)}</div>
            </div>
          </div>
        </section>

        {/* Table */}
        <section className="paylote-table-card">
          {/* Toolbar */}
          <div className="paylote-table-toolbar">
            <div className="paylote-tabs">
              {FILTERS.map((f) => (
                <button
                  key={f}
                  className={`paylote-tab${currentFilter === f ? " active" : ""}`}
                  onClick={() => setCurrentFilter(f)}
                >
                  {FILTER_LABELS[f]} ({f === "todos" ? payments.length : countByStatus(f)})
                </button>
              ))}
            </div>

            <div className="paylote-search-area">
              <div className="paylote-search-box">
                <TbSearch size={15} />
                <input
                  type="text"
                  placeholder="Buscar por favorecido, CPF/CNPJ, valor..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Select all */}
          <div className="paylote-selection-bar">
            <input
              type="checkbox"
              id="selectAll"
              onChange={(e) => toggleAll(e.target.checked)}
            />
            <label htmlFor="selectAll" className="paylote-apr-select-label">
              Selecionar todas ({visible.length})
            </label>
          </div>

          {/* Table */}
          <table>
            <thead>
              <tr>
                <th></th>
                <th>Favorecido</th>
                <th>Tipo</th>
                <th>Detalhes</th>
                <th>Vencimento</th>
                <th>Valor</th>
                <th>Status</th>
                <th>Situação</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 ? (
                <tr><td colSpan={9}>
                  <div className="paylote-empty-state">
                    <p>Nenhuma conta encontrada para o filtro selecionado.</p>
                  </div>
                </td></tr>
              ) : visible.map((item) => (
                <tr key={item.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={item.selected}
                      onChange={(e) => toggleItem(item.id, e.target.checked)}
                    />
                  </td>
                  <td>
                    <div className="paylote-payee-name">{item.payee}</div>
                    <div className="paylote-payee-doc">{item.document}</div>
                  </td>
                  <td>
                    <span className="paylote-type-badge">
                      {typeIcon(item.type)} {item.type}
                    </span>
                  </td>
                  <td>
                    <span className="paylote-details">{item.details}</span>
                  </td>
                  <td className={item.situation === "Vencido" ? "paylote-situation-vencido" : ""}>
                    {dateBR(item.dueDate)}
                  </td>
                  <td><strong>{formatValueToMonetary(item.amount)}</strong></td>
                  <td>
                    <span className={`paylote-badge ${badgeClass(item.status)}`}>
                      {PAYMENT_STATUS_LABELS[item.status] || item.status}
                    </span>
                  </td>
                  <td className={item.situation === "Vencido" ? "paylote-situation-vencido" : "paylote-situation-muted"}>
                    {item.situation}
                  </td>
                  <td>
                    {item.status === "Alerta" && (
                      <button
                        className="paylote-remove-btn"
                        title="Remover do lote"
                        onClick={() => removePayment(item.id)}
                      >
                        <TbTrash size={15} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
        </>)}
      </div>

      {/* Bottom approval bar */}
      <div className="paylote-bottom-bar">
        <div>
          <div className="paylote-bottom-title">
            {stats.selected.count} conta{stats.selected.count !== 1 ? "s" : ""} selecionada{stats.selected.count !== 1 ? "s" : ""}
          </div>
          <div className="paylote-bottom-value">{formatValueToMonetary(stats.selected.amount)}</div>
        </div>

        <div className="paylote-bottom-stat">
          <div className="paylote-bottom-circle" style={{ background: "#16a34a" }}>✓</div>
          <div>
            <div className="paylote-bottom-stat-value">{stats.selectedApproved.count}</div>
            <div className="paylote-bottom-stat-label">Aprovadas</div>
            <div className="paylote-bottom-stat-amount">{formatValueToMonetary(stats.selectedApproved.amount)}</div>
          </div>
        </div>

        <div className="paylote-bottom-stat">
          <div className="paylote-bottom-circle" style={{ background: "#f59e0b" }}>◷</div>
          <div>
            <div className="paylote-bottom-stat-value">{stats.selectedPending.count}</div>
            <div className="paylote-bottom-stat-label">Pendentes</div>
            <div className="paylote-bottom-stat-amount">{formatValueToMonetary(stats.selectedPending.amount)}</div>
          </div>
        </div>

        <div className="paylote-bottom-stat">
          <div className="paylote-bottom-circle" style={{ background: "var(--primary-color)" }}>!</div>
          <div>
            <div className="paylote-bottom-stat-value">{stats.selectedAlert.count}</div>
            <div className="paylote-bottom-stat-label">Com alerta</div>
            <div className="paylote-bottom-stat-amount">{formatValueToMonetary(stats.selectedAlert.amount)}</div>
          </div>
        </div>

        <div className="paylote-release-area">
          <button
            className="paylote-btn-success"
            onClick={handleRelease}
            disabled={!canApprove}
            title={approveBlockReason || undefined}
            style={!canApprove ? { opacity: 0.45, cursor: "not-allowed" } : undefined}
          >
            ✓ Aprovar e executar pagamentos
          </button>
          <div className="paylote-release-note">
            {approveBlockReason
              ? approveBlockReason
              : "As contas selecionadas serão aprovadas e os pagamentos realizados imediatamente"}
          </div>
        </div>
      </div>
    </div>

    {/* MFA Modal */}
    <ReactModal
      isOpen={mfaModalOpen}
      className="change-modal"
      overlayClassName="overlay-pix-modal"
    >
      <BoxModal
        title="Confirmar aprovação do lote"
        close={() => { setMfaModalOpen(false); setMfaCode(""); }}
        maxWidth={420}
      >
        <div style={{ padding: "0.5rem 0" }}>
          <p style={{ fontSize: "0.9rem", color: "#475569", marginBottom: "1.25rem" }}>
            Enviamos um código de confirmação via SMS. Insira o código abaixo para concluir a aprovação.
          </p>

          <div style={{ marginBottom: "1rem" }}>
            <label style={{ display: "block", fontWeight: 600, fontSize: "0.85rem", marginBottom: "0.4rem" }}>
              Código de confirmação
            </label>
            <input
              className="form-control"
              type="text"
              placeholder="Insira o código recebido por SMS"
              value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value)}
              maxLength={6}
              autoFocus
            />
          </div>

          <div style={{ fontSize: "0.82rem", color: "#64748b", marginBottom: "1.25rem" }}>
            {mfaDisabled ? (
              <span>Você pode solicitar um novo código em 0:{mfaCountdown < 10 ? `0${mfaCountdown}` : mfaCountdown} segundos</span>
            ) : (
              <span>Não recebeu o código?{" "}
                <button
                  onClick={handleResendMfa}
                  style={{ background: "none", border: "none", color: "var(--primary-color, #3b82f6)", cursor: "pointer", padding: 0, fontWeight: 600, textDecoration: "underline", fontSize: "0.82rem" }}
                >
                  Reenviar código
                </button>
              </span>
            )}
          </div>

          <div className="d-flex gap-3">
            <button
              className="btn btn-secondary flex-fill"
              onClick={() => { setMfaModalOpen(false); setMfaCode(""); }}
            >
              Cancelar
            </button>
            <button
              className="paylote-btn-success flex-fill"
              onClick={handleConfirmMfa}
            >
              Confirmar aprovação
            </button>
          </div>
        </div>
      </BoxModal>
    </ReactModal>
    </>
  );
}
